/**
 * Gaps in a drawn roof (pure, tested in gaps.test.ts; twin of app/design/sketch/gaps.py, shared cases in
 * apps/solar-backend/tests/design/fixtures/sketch_gaps.json).
 *
 * Why a drawn roof is not closed (docs/features/sketch.md § 3b):
 * - `weld`: two corners that almost meet but are separate vertices (a hip ending 10 cm from the ridge's end): the
 *   faces either side do not share an edge, so the 3D view shows a sliver between them.
 * - `split`: a corner lying on (or just off) a line that is not split there (a T-junction, a dormer or footprint edge
 *   that was not cut): the face across the line does not have the corner, its side runs past it.
 * - `dangling`: a line ending just short of (or just past) another line or corner, so the region it should close
 *   stays one face.
 * - `crossing`: two lines crossing without a corner (after dragging a corner across a line).
 * - `sliver`: a face thinner than a few centimetres, left between two lines drawn almost on top of each other.
 * - `height`: faces of one building meeting at a corner at different heights (the ridge drawn longer than where the
 *   planes meet, unequal eave heights): each face is a plane, so a step opens along their shared line.
 *
 * Every gap has a one-step fix (`closeGap`) and `closeGaps` fixes them all in order (crossings, welds, splits,
 * dangling ends, slivers, heights). Heights are made to meet by moving the corner to where the faces' planes meet
 * (least squares; a corner on the outline slides along it), by at most `MAX_MOVE_M`; a corner that cannot move (an
 * outside corner) gets its faces' eave heights evened to the largest face's. `settle` is the light version the
 * drawing tools run on the corners a gesture touched (welds and splits within the snap tolerance, heights within
 * `AUTO_MOVE_M`), so most gaps never appear.
 */
import type { MessageKey } from "../i18n/keys";
import { metres, type Text } from "./text.ts";
import type { Vec2 } from "../editor/geo.ts";
import { anchorIndex, closestOnSegment, crossing, nextId, planeZ, refaced, resolveSlopes, ringArea, type FaceSettings, type Sketch, type SkEdge, type SkFace } from "./skeleton.ts";

export const WELD_M = 0.25;
/** Corners joined by a line this short are one corner drawn twice; a longer short line may be meant (a skeleton's
 * short ridge between two hips of different pitches). */
export const TINY_LINE_M = 0.05;
export const SPLIT_M = 0.2;
export const DANGLING_M = 0.6;
export const SLIVER_WIDTH_M = 0.15;
export const SLIVER_AREA_M2 = 0.25;
export const HEIGHT_STEP_M = 0.03;
/** A step this tall between faces whose planes cannot meet by moving a corner is a deliberate level change. */
export const LEVEL_CHANGE_M = 0.5;
export const MAX_MOVE_M = 2;
export const AUTO_MOVE_M = 0.3;
const LAMBDA = 1e-6;
const COLLINEAR_SIN = Math.sin((2 * Math.PI) / 180);

export type GapKind = "crossing" | "weld" | "split" | "dangling" | "sliver" | "height";
export const GAP_ORDER: GapKind[] = ["crossing", "weld", "split", "dangling", "sliver", "height"];

export interface Gap {
  /** Stable for a given drawing (React keys, the selected gap). */
  id: string;
  kind: GapKind;
  /** Where it is, in plan (local metres). */
  at: Vec2;
  vertices: string[];
  edges: string[];
  faces: string[];
  /** Metres: the distance (weld, split, dangling), the width (sliver) or the step (height). */
  size: number;
  /** Whether `closeGap` can fix it. */
  fixable: boolean;
  /** dangling: what the end joins (a vertex or a line) or `trim` (a stub past a corner); height: `move`, `slide`, `even`. */
  mode?: string;
  /** Where the fix puts the corner (split, dangling onto a line, height move or slide). */
  target?: Vec2;
  /** dangling: the vertex or line it joins. */
  to?: string;
}

const dist = (a: Vec2, b: Vec2) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
const sorted = <T>(xs: T[]) => [...xs].sort();

// ----------------------------------------------------------------------------------------------- graph helpers
function degrees(s: Sketch): Map<string, number> {
  const d = new Map<string, number>();
  for (const e of s.edges) {
    d.set(e.a, (d.get(e.a) ?? 0) + 1);
    d.set(e.b, (d.get(e.b) ?? 0) + 1);
  }
  return d;
}

function usedVertices(s: Sketch): string[] {
  const used = new Set<string>();
  for (const e of s.edges) {
    used.add(e.a);
    used.add(e.b);
  }
  return sorted([...used].filter((v) => s.vertices[v]));
}

/** How many faces have each side (a pair of vertex ids) in their outline. */
function sideCounts(s: Sketch): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const f of s.faces) {
    const n = f.boundary.length;
    for (let i = 0; i < n; i++) {
      const key = pairKey(f.boundary[i]!, f.boundary[(i + 1) % n]!);
      out.set(key, [...(out.get(key) ?? []), f.id]);
    }
  }
  return out;
}

const structureOf = (f: SkFace) => f.structureId || "main";

/** z = a·x + b·y + c of each face's plane (its slope as the drawing resolves it). */
export function facePlanes(s: Sketch): Map<string, [number, number, number]> {
  const slopes = resolveSlopes(s);
  const out = new Map<string, [number, number, number]>();
  for (const f of s.faces) {
    const slope = slopes[f.id];
    const pts = f.boundary.map((v) => s.vertices[v]!);
    if (!slope || pts.some((p) => !p) || pts.length < 3) continue;
    const anchor = pts[anchorIndex(pts, slope.azimuth)]!;
    const z0 = planeZ(slope.pitch, slope.azimuth, anchor, slope.eaveHeight, [0, 0]);
    const zx = planeZ(slope.pitch, slope.azimuth, anchor, slope.eaveHeight, [1, 0]) - z0;
    const zy = planeZ(slope.pitch, slope.azimuth, anchor, slope.eaveHeight, [0, 1]) - z0;
    out.set(f.id, [zx, zy, z0]);
  }
  return out;
}

const zOf = (p: [number, number, number], q: Vec2) => p[0] * q[0] + p[1] * q[1] + p[2];

// --------------------------------------------------------------------------------------------------- finding
/** Every gap of the drawing, in fix order (crossings, welds, splits, dangling ends, slivers, heights). */
export function findGaps(s: Sketch): Gap[] {
  const out: Gap[] = [];
  const verts = usedVertices(s);
  const deg = degrees(s);
  const P = (v: string) => s.vertices[v]!;
  const edges = s.edges.filter((e) => s.vertices[e.a] && s.vertices[e.b] && e.a !== e.b);

  // crossings
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 1; j < edges.length; j++) {
      const e = edges[i]!, f = edges[j]!;
      if (e.a === f.a || e.a === f.b || e.b === f.a || e.b === f.b) continue;
      const hit = crossing(P(e.a), P(e.b), P(f.a), P(f.b));
      if (!hit) continue;
      out.push({ id: `crossing:${e.id}:${f.id}`, kind: "crossing", at: hit.point, vertices: [], edges: [e.id, f.id], faces: [], size: 0, fixable: true, target: hit.point });
    }
  }

  // welds: clusters of corners closer than WELD_M
  const parent = new Map(verts.map((v) => [v, v]));
  const root = (v: string): string => (parent.get(v) === v ? v : root(parent.get(v)!));
  const joined = new Set(edges.map((e) => pairKey(e.a, e.b)));
  for (let i = 0; i < verts.length; i++)
    for (let j = i + 1; j < verts.length; j++) {
      const d = dist(P(verts[i]!), P(verts[j]!));
      if (d < WELD_M && (d < TINY_LINE_M || !joined.has(pairKey(verts[i]!, verts[j]!)))) {
        const a = root(verts[i]!), b = root(verts[j]!);
        if (a !== b) parent.set(a < b ? b : a, a < b ? a : b);
      }
    }
  const clusters = new Map<string, string[]>();
  for (const v of verts) clusters.set(root(v), [...(clusters.get(root(v)) ?? []), v]);
  const welded = new Set<string>();
  for (const group of [...clusters.values()].filter((g) => g.length > 1).sort((a, b) => (a[0]! < b[0]! ? -1 : 1))) {
    const keeper = keeperOf(group, deg);
    const others = sorted(group.filter((v) => v !== keeper));
    group.forEach((v) => welded.add(v));
    out.push({ id: `weld:${[keeper, ...others].join(",")}`, kind: "weld", at: P(keeper), vertices: [keeper, ...others], edges: [], faces: [], size: Math.max(...others.map((v) => dist(P(v), P(keeper)))), fixable: true });
  }

  // splits: a corner on (or just off) a line that is not split there
  const split = new Set<string>();
  for (const v of verts) {
    if (welded.has(v)) continue;
    let best: { e: SkEdge; d: number; point: Vec2 } | null = null;
    for (const e of edges) {
      if (e.a === v || e.b === v) continue;
      const hit = closestOnSegment(P(v), P(e.a), P(e.b));
      if (hit.distance >= SPLIT_M || dist(P(v), P(e.a)) < WELD_M || dist(P(v), P(e.b)) < WELD_M) continue;
      if (!best || hit.distance < best.d - 1e-9) best = { e, d: hit.distance, point: hit.point };
    }
    if (!best) continue;
    split.add(v);
    out.push({ id: `split:${v}:${best.e.id}`, kind: "split", at: best.point, vertices: [v], edges: [best.e.id], faces: [], size: best.d, fixable: true, target: best.point });
  }

  // dangling ends: a loose line end just short of (or just past) a corner or a line
  for (const v of verts) {
    if (deg.get(v) !== 1 || welded.has(v) || split.has(v)) continue;
    const e0 = edges.find((e) => e.a === v || e.b === v);
    if (!e0) continue;
    const w = e0.a === v ? e0.b : e0.a;
    const length = dist(P(v), P(w));
    if (length < DANGLING_M && (deg.get(w) ?? 0) >= 3) {
      out.push({ id: `dangling:${v}`, kind: "dangling", at: P(v), vertices: [v], edges: [e0.id], faces: [], size: length, fixable: true, mode: "trim" });
      continue;
    }
    let vertex: { id: string; d: number } | null = null;
    for (const u of verts) {
      if (u === v || u === w) continue;
      const d = dist(P(u), P(v));
      if (d < DANGLING_M && (!vertex || d < vertex.d - 1e-9)) vertex = { id: u, d };
    }
    let line: { e: SkEdge; d: number; point: Vec2 } | null = null;
    for (const e of edges) {
      if (e.a === v || e.b === v) continue;
      const hit = closestOnSegment(P(v), P(e.a), P(e.b));
      if (hit.t <= 1e-6 || hit.t >= 1 - 1e-6 || hit.distance >= DANGLING_M) continue;
      if (!line || hit.distance < line.d - 1e-9) line = { e, d: hit.distance, point: hit.point };
    }
    if (vertex && (!line || vertex.d <= line.d + 0.05))
      out.push({ id: `dangling:${v}`, kind: "dangling", at: P(v), vertices: [v], edges: [e0.id], faces: [], size: vertex.d, fixable: true, mode: "vertex", to: vertex.id, target: P(vertex.id) });
    else if (line)
      out.push({ id: `dangling:${v}`, kind: "dangling", at: P(v), vertices: [v], edges: [e0.id], faces: [], size: line.d, fixable: true, mode: "line", to: line.e.id, target: line.point });
  }

  // slivers
  const sides = sideCounts(s);
  const slivers = new Set<string>();
  for (const f of s.faces) {
    const pts = f.boundary.map(P);
    if (pts.some((p) => !p)) continue;
    const area = Math.abs(ringArea(pts));
    const perimeter = pts.reduce((sum, p, i) => sum + dist(p, pts[(i + 1) % pts.length]!), 0);
    const width = perimeter > 0 ? (2 * area) / perimeter : 0;
    if (area >= SLIVER_AREA_M2 && width >= SLIVER_WIDTH_M) continue;
    slivers.add(f.id);
    const shared = new Map<string, { length: number; edges: string[] }>();
    const n = f.boundary.length;
    for (let i = 0; i < n; i++) {
      const a = f.boundary[i]!, b = f.boundary[(i + 1) % n]!;
      for (const g of sides.get(pairKey(a, b)) ?? []) {
        if (g === f.id) continue;
        const edge = edges.find((e) => pairKey(e.a, e.b) === pairKey(a, b));
        const cur = shared.get(g) ?? { length: 0, edges: [] };
        shared.set(g, { length: cur.length + dist(P(a), P(b)), edges: edge ? [...cur.edges, edge.id] : cur.edges });
      }
    }
    let host: string | null = null;
    for (const [g, x] of [...shared.entries()].sort((p, q) => (p[0] < q[0] ? -1 : 1))) if (!host || x.length > shared.get(host)!.length + 1e-9) host = g;
    const c = centroidOf(pts);
    out.push({ id: `sliver:${f.id}`, kind: "sliver", at: c, vertices: [], edges: host ? sorted(shared.get(host)!.edges) : [], faces: host ? [f.id, host] : [f.id], size: width, fixable: !!host && shared.get(host)!.edges.length > 0 });
  }

  // heights: faces of one building meeting at a corner at different heights
  const planes = facePlanes(s);
  for (const v of verts) {
    const groups = new Map<string, SkFace[]>();
    for (const f of s.faces) if (f.boundary.includes(v) && planes.has(f.id) && !slivers.has(f.id)) groups.set(structureOf(f), [...(groups.get(structureOf(f)) ?? []), f]);
    for (const [, group] of [...groups.entries()].sort((p, q) => (p[0] < q[0] ? -1 : 1))) {
      if (group.length < 2) continue;
      const zs = group.map((f) => zOf(planes.get(f.id)!, P(v)));
      const step = Math.max(...zs) - Math.min(...zs);
      if (step <= HEIGHT_STEP_M) continue;
      const fix = heightFix(s, v, group, planes, sides);
      if (!fix && step >= LEVEL_CHANGE_M) continue; // a deliberate level change (the 3D view walls it)
      out.push({
        id: `height:${v}:${sorted(group.map((f) => f.id)).join(",")}`,
        kind: "height",
        at: P(v),
        vertices: [v],
        edges: [],
        faces: sorted(group.map((f) => f.id)),
        size: step,
        fixable: !!fix,
        ...(fix ? { mode: fix.mode, ...(fix.target ? { target: fix.target } : {}) } : {}),
      });
    }
  }
  return out;
}

function keeperOf(group: string[], deg: Map<string, number>): string {
  return [...group].sort((a, b) => (deg.get(b) ?? 0) - (deg.get(a) ?? 0) || (a < b ? -1 : 1))[0]!;
}

function centroidOf(pts: Vec2[]): Vec2 {
  const a = ringArea(pts);
  if (Math.abs(a) < 1e-9) return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
  let cx = 0, cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!, q = pts[(i + 1) % pts.length]!;
    const k = p[0] * q[1] - q[0] * p[1];
    cx += (p[0] + q[0]) * k;
    cy += (p[1] + q[1]) * k;
  }
  return [cx / (6 * a), cy / (6 * a)];
}

// ---------------------------------------------------------------------------------------- making heights meet
interface HeightFix {
  mode: "move" | "slide" | "even";
  target?: Vec2;
  /** even: each face's new eave height. */
  eaves?: Record<string, number>;
}

/** How the faces of `group` can meet at corner `v`: move it to where their planes meet (inside the roof), slide it
 * along a straight outline, or even the faces' eave heights (an outside corner); null when none works. */
function heightFix(s: Sketch, v: string, group: SkFace[], planes: Map<string, [number, number, number]>, sides: Map<string, string[]>): HeightFix | null {
  const p0 = s.vertices[v]!;
  const ps = group.map((f) => planes.get(f.id)!);
  const meets = (q: Vec2) => {
    const zs = ps.map((p) => zOf(p, q));
    return Math.max(...zs) - Math.min(...zs) <= HEIGHT_STEP_M / 3;
  };
  // the outline sides at v (sides with one face)
  const outline: string[] = [];
  for (const e of s.edges) {
    if (e.a !== v && e.b !== v) continue;
    const faces = sides.get(pairKey(e.a, e.b)) ?? [];
    if (faces.length === 1) outline.push(e.a === v ? e.b : e.a);
  }
  const keepsShape = (q: Vec2) =>
    s.faces.every((f) => {
      if (!f.boundary.includes(v)) return true;
      const before = ringArea(f.boundary.map((id) => s.vertices[id]!));
      const after = ringArea(f.boundary.map((id) => (id === v ? q : s.vertices[id]!)));
      return Math.sign(after) === Math.sign(before) && Math.abs(after) > SLIVER_AREA_M2 / 2;
    });
  if (!outline.length) {
    // inside the roof: least squares over (x, y, z), held near where it is
    const A = [[LAMBDA, 0, 0], [0, LAMBDA, 0], [0, 0, 0]];
    const b = [LAMBDA * p0[0], LAMBDA * p0[1], 0];
    for (const [a1, b1, c1] of ps) {
      const row = [a1, b1, -1];
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) A[i]![j]! += row[i]! * row[j]!;
        b[i]! -= row[i]! * c1;
      }
    }
    const x = solve3(A, b);
    if (!x) return null;
    const q: Vec2 = [x[0]!, x[1]!];
    if (dist(q, p0) > MAX_MOVE_M || !meets(q) || !keepsShape(q)) return evenFix(s, v, group, ps);
    return { mode: "move", target: q };
  }
  if (outline.length === 2) {
    const d1: Vec2 = [s.vertices[outline[0]!]![0] - p0[0], s.vertices[outline[0]!]![1] - p0[1]];
    const d2: Vec2 = [s.vertices[outline[1]!]![0] - p0[0], s.vertices[outline[1]!]![1] - p0[1]];
    const l1 = Math.hypot(...d1), l2 = Math.hypot(...d2);
    if (l1 > 1e-9 && l2 > 1e-9 && Math.abs(d1[0] * d2[1] - d1[1] * d2[0]) / (l1 * l2) < COLLINEAR_SIN && d1[0] * d2[0] + d1[1] * d2[1] < 0) {
      // a straight outline through v: slide along it, minimising Σ (g·t + c − z)² + λt²
      const u: Vec2 = [d1[0] / l1, d1[1] / l1];
      let stt = LAMBDA, stz = 0, szz = 0, sct = 0, scz = 0;
      for (const p of ps) {
        const g = p[0] * u[0] + p[1] * u[1];
        const c = zOf(p, p0);
        stt += g * g; stz -= g; szz += 1; sct -= g * c; scz += c;
      }
      const det = stt * szz - stz * stz;
      if (Math.abs(det) < 1e-12) return evenFix(s, v, group, ps);
      const t = (sct * szz - stz * scz) / det;
      const q: Vec2 = [p0[0] + u[0] * t, p0[1] + u[1] * t];
      if (Math.abs(t) > MAX_MOVE_M || t >= l1 - WELD_M || -t >= l2 - WELD_M || !meets(q) || !keepsShape(q)) return evenFix(s, v, group, ps);
      return { mode: "slide", target: q };
    }
  }
  return evenFix(s, v, group, ps);
}

/** An outside corner on the faces' eaves: their eave heights evened to the building's usual eave height (the most
 * common among its faces with an eave, to the centimetre; a tie goes to the larger total area). */
function evenFix(s: Sketch, v: string, group: SkFace[], ps: [number, number, number][]): HeightFix | null {
  const slopes = resolveSlopes(s);
  // only faces with an eave of their own take a new eave height (the others follow the faces they meet), and only
  // where the corner is on their eave line
  const p0 = s.vertices[v]!;
  if (group.some((f, i) => !slopes[f.id]?.eave || Math.abs(zOf(ps[i]!, p0) - slopes[f.id]!.eaveHeight) > 1e-6)) return null;
  const zs = ps.map((p) => zOf(p, p0));
  if (Math.max(...zs) - Math.min(...zs) >= LEVEL_CHANGE_M) return null; // not a slip: the drawing is wrong there
  const structure = structureOf(group[0]!);
  const votes = new Map<number, { n: number; area: number }>();
  for (const f of s.faces) {
    if (structureOf(f) !== structure || !slopes[f.id]?.eave) continue;
    const cm = Math.floor(slopes[f.id]!.eaveHeight * 100 + 0.5);
    const area = Math.abs(ringArea(f.boundary.map((id) => s.vertices[id]!)));
    const cur = votes.get(cm) ?? { n: 0, area: 0 };
    votes.set(cm, { n: cur.n + 1, area: cur.area + area });
  }
  let best: [number, { n: number; area: number }] | null = null;
  for (const entry of [...votes.entries()].sort((a, b) => a[0] - b[0])) if (!best || entry[1].n > best[1].n || (entry[1].n === best[1].n && entry[1].area > best[1].area + 1e-9)) best = entry;
  if (!best) return null;
  const target = best[0] / 100;
  const eaves: Record<string, number> = {};
  for (const f of group) if (Math.abs(slopes[f.id]!.eaveHeight - target) > 1e-9) eaves[f.id] = target;
  if (!Object.keys(eaves).length) return null;
  return { mode: "even", eaves };
}

function solve3(A: number[][], b: number[]): number[] | null {
  const m = A.map((row, i) => [...row, b[i]!]);
  for (let c = 0; c < 3; c++) {
    let p = c;
    for (let r = c + 1; r < 3; r++) if (Math.abs(m[r]![c]!) > Math.abs(m[p]![c]!)) p = r;
    if (Math.abs(m[p]![c]!) < 1e-12) return null;
    [m[c], m[p]] = [m[p]!, m[c]!];
    for (let r = 0; r < 3; r++) {
      if (r === c) continue;
      const k = m[r]![c]! / m[c]![c]!;
      for (let j = c; j < 4; j++) m[r]![j]! -= k * m[c]![j]!;
    }
  }
  return [m[0]![3]! / m[0]![0]!, m[1]![3]! / m[1]![1]!, m[2]![3]! / m[2]![2]!];
}

// ----------------------------------------------------------------------------------------------------- fixing
/** Faces with vertex ids renamed (`map`) and repeated corners dropped, so their settings survive refacing. */
function renamed(faces: SkFace[], map: Record<string, string>): SkFace[] {
  return faces.map((f) => {
    const ring = f.boundary.map((v) => map[v] ?? v).filter((v, i, r) => v !== r[(i + 1) % r.length]);
    return { ...f, boundary: ring };
  });
}

/** Faces with corner `v` inserted where their outline runs a→b (a line split at v). */
function inserted(faces: SkFace[], a: string, b: string, v: string): SkFace[] {
  return faces.map((f) => {
    const n = f.boundary.length;
    for (let i = 0; i < n; i++) {
      const p = f.boundary[i]!, q = f.boundary[(i + 1) % n]!;
      if ((p === a && q === b) || (p === b && q === a)) return { ...f, boundary: [...f.boundary.slice(0, i + 1), v, ...f.boundary.slice(i + 1)] };
    }
    return f;
  });
}

/** Lines without self-loops and repeats (the first of a pair stays), and the vertices nothing uses dropped. */
function tidy(s: Sketch): Sketch {
  const seen = new Set<string>();
  const edges = s.edges.filter((e) => {
    if (e.a === e.b) return false;
    const k = pairKey(e.a, e.b);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const used = new Set(edges.flatMap((e) => [e.a, e.b]));
  const vertices = Object.fromEntries(Object.entries(s.vertices).filter(([id]) => used.has(id)));
  return { ...s, vertices, edges };
}

/** Corners among `ids` left between two lines of one type running straight on (after a line was removed) taken out:
 * the two lines become one. */
function dissolve(s: Sketch, ids: string[]): Sketch {
  let current = s;
  for (const v of sorted([...new Set(ids)])) {
    const at = current.edges.filter((e) => e.a === v || e.b === v);
    if (at.length !== 2 || at[0]!.type !== at[1]!.type) continue;
    const [x, y] = at.map((e) => (e.a === v ? e.b : e.a)) as [string, string];
    const p = current.vertices[v]!, a = current.vertices[x]!, b = current.vertices[y]!;
    const d1: Vec2 = [a[0] - p[0], a[1] - p[1]], d2: Vec2 = [b[0] - p[0], b[1] - p[1]];
    const l = Math.hypot(...d1) * Math.hypot(...d2);
    if (!l || Math.abs(d1[0] * d2[1] - d1[1] * d2[0]) / l >= COLLINEAR_SIN || d1[0] * d2[0] + d1[1] * d2[1] >= 0) continue;
    const edges = current.edges.flatMap((e) => (e === at[0] ? [{ ...e, a: x, b: y }] : e === at[1] ? [] : [e]));
    const faces = current.faces.map((f) => ({ ...f, boundary: f.boundary.filter((id) => id !== v) }));
    const vertices = { ...current.vertices };
    delete vertices[v];
    current = { ...current, vertices, edges, faces };
  }
  return current;
}

function weldInto(s: Sketch, keeper: string, others: string[], defaults: FaceSettings): Sketch {
  const map = Object.fromEntries(others.map((v) => [v, keeper]));
  const edges = s.edges.map((e) => ({ ...e, a: map[e.a] ?? e.a, b: map[e.b] ?? e.b }));
  return refaced(tidy({ ...s, edges, faces: renamed(s.faces, map) }), defaults);
}

function splitAt(s: Sketch, v: string, edgeId: string, point: Vec2, defaults: FaceSettings): Sketch {
  const e = s.edges.find((x) => x.id === edgeId);
  if (!e) return s;
  const id2 = nextId("e", s.edges.map((x) => x.id));
  const edges = s.edges.flatMap((x) => (x.id === edgeId ? [{ ...x, b: v }, { id: id2, a: v, b: e.b, type: x.type }] : [x]));
  return refaced(tidy({ ...s, vertices: { ...s.vertices, [v]: point }, edges, faces: inserted(s.faces, e.a, e.b, v) }), defaults);
}

/** The drawing with one gap fixed (one undo step); unchanged when it cannot be. */
export function closeGap(s: Sketch, gap: Gap, defaults: FaceSettings): Sketch {
  if (!gap.fixable) return s;
  switch (gap.kind) {
    case "crossing": {
      const [e1, e2] = gap.edges as [string, string];
      const v = nextId("v", Object.keys(s.vertices));
      const a = s.edges.find((x) => x.id === e1), b = s.edges.find((x) => x.id === e2);
      if (!a || !b) return s;
      let next = splitAt(s, v, e1, gap.target!, defaults);
      next = splitAt(next, v, e2, gap.target!, defaults);
      return next;
    }
    case "weld":
      return weldInto(s, gap.vertices[0]!, gap.vertices.slice(1), defaults);
    case "split":
      return splitAt(s, gap.vertices[0]!, gap.edges[0]!, gap.target!, defaults);
    case "dangling": {
      const v = gap.vertices[0]!;
      if (gap.mode === "trim") {
        const edges = s.edges.filter((e) => e.id !== gap.edges[0]);
        return refaced(tidy({ ...s, edges }), defaults);
      }
      if (gap.mode === "vertex") return weldInto(s, gap.to!, [v], defaults);
      return splitAt(s, v, gap.to!, gap.target!, defaults);
    }
    case "sliver": {
      const drop = new Set(gap.edges);
      const [sliver, host] = gap.faces as [string, string];
      // the merged region keeps the host's id and settings
      const faces = s.faces.filter((f) => f.id !== sliver);
      const ends = s.edges.filter((e) => drop.has(e.id)).flatMap((e) => [e.a, e.b]);
      const next = refaced(dissolve(tidy({ ...s, edges: s.edges.filter((e) => !drop.has(e.id)), faces }), ends), defaults);
      const hostFace = s.faces.find((f) => f.id === host)!;
      const overlap = (f: SkFace) => f.boundary.filter((v) => hostFace.boundary.includes(v)).length;
      const merged = [...next.faces].sort((a, b) => overlap(b) - overlap(a) || (a.id < b.id ? -1 : 1))[0];
      if (!merged || next.faces.some((f) => f.id === host && f !== merged)) return next;
      return { ...next, faces: next.faces.map((f) => (f === merged ? { ...hostFace, boundary: f.boundary } : f)) };
    }
    case "height": {
      const v = gap.vertices[0]!;
      if ((gap.mode === "move" || gap.mode === "slide") && gap.target) return { ...s, vertices: { ...s.vertices, [v]: gap.target } };
      if (gap.mode === "even") {
        const group = s.faces.filter((f) => gap.faces.includes(f.id));
        const fix = evenFix(s, v, group, group.map((f) => facePlanes(s).get(f.id)!));
        if (!fix?.eaves) return s;
        return { ...s, faces: s.faces.map((f) => (f.id in fix.eaves! ? { ...f, eaveHeight: fix.eaves![f.id]! } : f)) };
      }
      return s;
    }
  }
}

/** Every gap fixed that can be, in order, finding them again after each fix (one undo step for all). */
export function closeGaps(s: Sketch, defaults: FaceSettings, maxSteps = 80): { sketch: Sketch; closed: number; left: Gap[] } {
  let current = s;
  let closed = 0;
  const skipped = new Set<string>();
  for (let step = 0; step < maxSteps; step++) {
    const gaps = findGaps(current);
    const ordered = GAP_ORDER.flatMap((k) => gaps.filter((g) => g.kind === k));
    // even the eaves before moving corners: the corners then move to where the evened planes meet
    const next = [...ordered.filter((g) => g.kind !== "height"), ...ordered.filter((g) => g.mode === "even"), ...ordered.filter((g) => g.kind === "height" && g.mode !== "even")].find((g) => g.fixable && !skipped.has(g.id));
    if (!next) break;
    const fixed = closeGap(current, next, defaults);
    if (fixed === current || same(fixed, current)) {
      skipped.add(next.id);
      continue;
    }
    current = fixed;
    closed++;
  }
  return { sketch: current, closed, left: findGaps(current) };
}

function same(a: Sketch, b: Sketch): boolean {
  return JSON.stringify([a.vertices, a.edges, a.faces]) === JSON.stringify([b.vertices, b.edges, b.faces]);
}

/**
 * The light fix the drawing tools run after a gesture, on the corners it touched: a corner within `weld` of another
 * joins it, one within `SPLIT_M` of a line splits it, and faces meeting at it are made to meet in height when that
 * moves it by at most `AUTO_MOVE_M`. Anything larger is left for "Close gaps".
 */
export function settle(s: Sketch, touched: string[], defaults: FaceSettings, weld = WELD_M): Sketch {
  let current = s;
  for (const v0 of touched) {
    let v = v0;
    if (!current.vertices[v]) continue;
    const p = current.vertices[v]!;
    const deg = degrees(current);
    let near: { id: string; d: number } | null = null;
    for (const u of usedVertices(current)) {
      if (u === v) continue;
      const d = dist(current.vertices[u]!, p);
      if (d < weld && (!near || d < near.d)) near = { id: u, d };
    }
    if (near) {
      const keeper = keeperOf([v, near.id], deg);
      current = weldInto(current, keeper, [keeper === v ? near.id : v], defaults);
      v = keeper;
    } else {
      const gap = findGaps(current).find((g) => g.kind === "split" && g.vertices[0] === v);
      if (gap) current = closeGap(current, gap, defaults);
    }
    const height = findGaps(current).find((g) => g.kind === "height" && g.vertices[0] === v && g.fixable && (g.mode === "move" || g.mode === "slide"));
    if (height?.target && dist(height.target, current.vertices[v]!) <= AUTO_MOVE_M) current = closeGap(current, height, defaults);
  }
  return current;
}

/** The faces' heights at a corner (3D highlight of a height gap): [lowest, highest]. */
export function stepAt(s: Sketch, gap: Gap): [number, number] | null {
  if (gap.kind !== "height") return null;
  const planes = facePlanes(s);
  const zs = gap.faces.map((f) => planes.get(f)).filter(Boolean).map((p) => zOf(p!, gap.at));
  return zs.length ? [Math.min(...zs), Math.max(...zs)] : null;
}

const KIND_LABEL: Record<GapKind, MessageKey> = {
  crossing: "sketch.gap.crossing",
  weld: "sketch.gap.weld",
  split: "sketch.gap.split",
  dangling: "sketch.gap.dangling",
  sliver: "sketch.gap.sliver",
  height: "sketch.gap.height",
};

/** What the side panel says about a gap: a title and a detail with its size. */
export function gapText(tx: Text, g: Gap): { title: string; detail: string } {
  const size = metres(tx, g.size);
  const title = tx.t(g.kind === "dangling" && g.mode === "trim" ? "sketch.gap.runsPast" : KIND_LABEL[g.kind]);
  const detail =
    g.kind === "crossing" ? tx.t("sketch.gap.fix.crossing")
    : g.kind === "weld" ? tx.t("sketch.gap.fix.weld", { size })
    : g.kind === "split" ? tx.t("sketch.gap.fix.split", { size })
    : g.kind === "dangling" ? tx.t(g.mode === "trim" ? "sketch.gap.fix.trim" : "sketch.gap.fix.extend", { size })
    : g.kind === "sliver" ? tx.t("sketch.gap.fix.sliver", { size })
    : !g.fixable ? tx.t("sketch.gap.fix.checkPitches", { size })
    : g.mode === "even" ? tx.t("sketch.gap.fix.evenEaves", { size })
    : g.target ? tx.t("sketch.gap.fix.moveCorner", { size, by: metres(tx, dist(g.target, g.at)) })
    : tx.t("sketch.gap.fix.moveCornerAnywhere", { size });
  return { title, detail };
}

/** The headline of a check: "Roof closed" or "2 gaps found". */
export function gapSummary(tx: Text, gaps: Gap[]): string {
  if (!gaps.length) return tx.t("sketch.gap.roofClosed");
  return tx.t("sketch.gap.found", { count: gaps.length });
}

